import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api-client';
import { useHeaderAction } from '@/lib/header-action-context';
import { useListFilters } from '@/hooks/use-list-filters';
import { usePageTitle } from '@/hooks/use-page-title';
import { Card } from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Clock,
    CreditCard,
    Package,
    Plus,
} from 'lucide-react';
import ProductFormWizard from '@/components/marketplace/ProductFormWizard';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

interface Product {
    id: string;
    application_id: string;
    title: string;
    summary?: string;
    listing_status: string;
    stripe_product_id?: string;
    created: string;
}

interface Application {
    id: string;
    app_key: string;
    name?: string | null;
}

const statusBadgeStyles: Record<string, string> = {
    published: 'bg-emerald-100 text-emerald-700',
    active: 'bg-emerald-100 text-emerald-700',
    draft: 'bg-slate-100 text-slate-700',
    archived: 'bg-amber-100 text-amber-700',
};

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created' },
] as const;

export default function MarketplaceProductsPage() {
    const { setHeaderAction, setHeaderTitle } = useHeaderAction();
    const { filters, setFilter } = useListFilters();
    usePageTitle('Products');

    const titleIcon = useMemo(
        () => (
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-700">
                <Package size={16} />
            </div>
        ),
        [],
    );

    useEffect(() => {
        setHeaderTitle({
            icon: titleIcon,
            title: 'Products',
            subtitle:
                'Select capabilities from your applications and publish them as marketplace product listings.',
        });
        return () => setHeaderTitle(null);
    }, [setHeaderTitle, titleIcon]);

    const [products, setProducts] = useState<Product[]>([]);
    const [applications, setApplications] = useState<Application[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDrawer, setShowCreateDrawer] = useState(false);

    const appMap = Object.fromEntries(
        applications.map((a) => [a.id, a.name || a.app_key])
    );

    const fetchProducts = async () => {
        try {
            const params: Record<string, string> = {};
            if (filters.status) params.status = filters.status;

            const [productsRes, appsRes] = await Promise.all([
                api.get('/billing/products', { params }),
                api.get('/applications?page=1'),
            ]);
            const productsData = productsRes.data;
            const list = productsData?.products ?? (Array.isArray(productsData) ? productsData : []);
            setProducts(list ?? []);

            const appsData = appsRes.data?.data ?? (Array.isArray(appsRes.data) ? appsRes.data : []);
            setApplications(appsData ?? []);
        } catch (err) {
            console.error('MarketplaceProductsPage.fetchProducts: failed', err);
            setProducts([]);
            setApplications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setHeaderAction(
            <button
                type="button"
                onClick={() => setShowCreateDrawer(true)}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
                <Plus size={14} />
                Add Product
            </button>,
        );
        return () => setHeaderAction(null);
    }, [setHeaderAction]);

    useEffect(() => {
        fetchProducts();
    }, [filters.status]);

    const handleSort = (field: string) => {
        if (filters.sort === field) {
            setFilter('direction', filters.direction === 'asc' ? 'desc' : 'asc');
        } else {
            setFilter('sort', field);
            setFilter('direction', 'asc');
        }
    };

    const filteredAndSortedProducts = useMemo(() => {
        let list = [...products];

        if (filters.search) {
            const q = filters.search.toLowerCase();
            list = list.filter((p) => {
                const title = (p.title ?? '').toLowerCase();
                const summary = (p.summary ?? '').toLowerCase();
                const appName = (appMap[p.application_id] ?? '').toLowerCase();
                const appKey = (p.application_id ?? '').toLowerCase();
                return (
                    title.includes(q) ||
                    summary.includes(q) ||
                    appName.includes(q) ||
                    appKey.includes(q)
                );
            });
        }

        const sort = filters.sort || 'name';
        const dir = filters.direction === 'desc' ? -1 : 1;
        list.sort((a, b) => {
            let cmp = 0;
            if (sort === 'name') {
                cmp = (a.title ?? '').localeCompare(b.title ?? '');
            } else if (sort === 'status') {
                cmp = (a.listing_status ?? '').localeCompare(b.listing_status ?? '');
            } else if (sort === 'created') {
                cmp =
                    new Date(a.created).getTime() -
                    new Date(b.created).getTime();
            }
            return cmp * dir;
        });

        return list;
    }, [products, appMap, filters.search, filters.sort, filters.direction]);

    const hasActiveFilters = filters.search || filters.status;

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden" style={{ height: '100%' }}>
            <CapabilityListPanel<Product>
                items={filteredAndSortedProducts}
                sortOptions={SORT_OPTIONS.map((option) => ({
                    key: option.key,
                    label: option.label,
                }))}
                sortField={filters.sort}
                sortDirection={filters.direction}
                onSort={handleSort}
                searchQuery={filters.search}
                onSearchChange={(value) => setFilter('search', value)}
                searchPlaceholder="Search products by title, summary, or app..."
                allTags={[]}
                selectedTags={new Set<string>()}
                onToggleTag={() => {}}
                onClearAllTags={() => {}}
                onSelectAllTags={() => {}}
                showTagsFilter={false}
                extraToolbar={
                    <Select
                        value={filters.status || 'all'}
                        onValueChange={(v) => setFilter('status', v === 'all' ? '' : v)}
                    >
                        <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
                            <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                }
                isLoading={loading}
                loadingMessage="Loading products..."
                emptyStateVariant="glass"
                emptyMessage={
                    products.length === 0
                        ? 'No products yet. Create one to get started.'
                        : 'No products match your filters.'
                }
                pagination={
                    filteredAndSortedProducts.length > 0 ? (
                        <div className="border-t border-slate-200 bg-slate-50 px-6 py-3">
                            <div className="text-sm text-slate-500">
                                Showing{' '}
                                <span className="font-medium text-slate-700">
                                    {filteredAndSortedProducts.length}
                                </span>{' '}
                                {filteredAndSortedProducts.length === 1 ? 'product' : 'products'}
                                {products.length !== filteredAndSortedProducts.length && (
                                    <span className="ml-1">(filtered from {products.length})</span>
                                )}
                            </div>
                        </div>
                    ) : undefined
                }
                renderCard={(p) => {
                    const badgeStyle =
                        statusBadgeStyles[p.listing_status] || 'bg-slate-100 text-slate-700';
                    return (
                        <Link
                            key={p.id}
                            to={`/settings/marketplace/${p.id}`}
                            className="block"
                        >
                            <Card
                                className="relative w-full min-h-32 border border-slate-200 border-l-4 border-l-violet-400 p-3 text-foreground/90 shadow-sm transition-all duration-200 hover:cursor-pointer hover:border-slate-300 hover:bg-slate-50 hover:shadow-md hover:shadow-violet-100/50"
                            >
                                <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
                                    <span
                                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase ${badgeStyle}`}
                                    >
                                        {p.listing_status}
                                    </span>
                                </div>

                                <div className="flex flex-row items-start gap-2">
                                    <div className="flex shrink-0 items-center justify-center rounded-md bg-violet-50 p-1.5 text-violet-600">
                                        <Package size={18} />
                                    </div>
                                    <div className="flex flex-1 flex-col">
                                        <div className="text-left text-sm font-bold leading-tight text-slate-800">
                                            {p.title}
                                        </div>
                                        <div className="text-left text-[10px] text-slate-500">
                                            <span className="font-mono text-[10px]">
                                                {appMap[p.application_id] ?? p.application_id ?? '—'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-auto flex items-center gap-3 border-t border-slate-100 pt-1.5 text-[10px] text-slate-500">
                                    {p.stripe_product_id && (
                                        <div className="flex items-center gap-1 text-slate-500">
                                            <CreditCard size={10} />
                                            <span className="max-w-[120px] truncate">
                                                {p.stripe_product_id}
                                            </span>
                                        </div>
                                    )}
                                    <div className="ml-auto flex items-center gap-1 text-slate-400">
                                        <Clock size={10} />
                                        <span>{new Date(p.created).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </Card>
                        </Link>
                    );
                }}
            />

            <ProductFormWizard
                isOpen={showCreateDrawer}
                onClose={() => setShowCreateDrawer(false)}
                onProductCreated={fetchProducts}
            />
        </div>
    );
}
